import { useState, type ChangeEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { message, Input, Modal, Typography } from 'antd';
import styled from 'styled-components';
import type { CustomArea, SavedPolygons } from '../../../../types/domain';
import { useSavedPolygonData } from '../../../../hooks/data/useSavedPolygonData';

type Props = {
  customArea: CustomArea | null;
  isOpen: boolean;
  onClose: () => void;
};

const CustomAreaSaveModal = ({
  customArea,
  isOpen,
  onClose,
}: Props): ReactNode => {
  const [name, setName] = useState<string>(customArea?.label || '');
  const [isComposing, setIsComposing] = useState<boolean>(false);

  const { t } = useTranslation();
  const { createSavedPolygon, isUpdating } = useSavedPolygonData();
  const { savedPolygons } = useSavedPolygonData();

  const isDuplicateName = savedPolygons.some(
    (polygon: SavedPolygons): boolean => {
      return polygon.name === name;
    }
  );

  const saveCustomArea = async (): Promise<void> => {
    if (!customArea) {
      return;
    }

    await createSavedPolygon({
      name: name,
      type: customArea.type,
      geometry: customArea.geometry,
      radius: customArea.radius,
      center: customArea.center,
      minutes: customArea.minutes,
    })
      .then((): void => {
        message.success(t('message.savedCustomArea', { name }));
        onClose();
      })
      .catch((): void => {
        message.error(t('message.failedToSaveCustomArea'));
      });
  };

  return (
    <Modal
      title={t('saveArea')}
      cancelText={t('cancel')}
      okText={t('save')}
      open={isOpen}
      okButtonProps={{
        disabled: name.trim() === '' || isUpdating,
        loading: isUpdating,
      }}
      onOk={saveCustomArea}
      onCancel={(): void => {
        setName(customArea?.label || '');
        onClose();
      }}
      centered
      destroyOnClose
    >
      <StyledInput
        autoFocus
        value={name}
        status={isDuplicateName ? 'warning' : undefined}
        onChange={(event: ChangeEvent<HTMLInputElement>): void => {
          setName(event.target.value);
        }}
        onCompositionStart={(): void => {
          setIsComposing(true);
        }}
        onCompositionEnd={(): void => {
          setIsComposing(false);
        }}
        onPressEnter={(): void => {
          if (isComposing || name.trim() === '') {
            return;
          }
          saveCustomArea();
        }}
      />
      {isDuplicateName && (
        <Typography.Text
          type="warning"
          style={{ marginTop: '4px', display: 'block' }}
        >
          {t('message.customAreaNameDuplicate')}
        </Typography.Text>
      )}
    </Modal>
  );
};

const StyledInput = styled(Input)`
  font-size: 12px;
  padding-inline: 2px;
  height: 32px;
`;

export { CustomAreaSaveModal };
